import { ReactNode, Ref, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { NoNullish } from '../../types';
import { Typography } from '../Typography';
import { SearchInputField } from '../InputField';
import { Tab } from '../Tab';
import { Dropdown, DropdownOption } from '../Dropdown';
import { List } from 'react-window';
import { PlayerFiltersViewRowItem } from './PlayerFiltersViewRowItem';
import { AddPlayer, Loading } from '../svgs';
import { NextFixtureItem, PlayerProfileModal } from '../../modals';
import { TeamShirt } from '../TeamShirt';
import { CountryFlag } from '../CountryFlag';
import { toCurrencyPriceMillions, toPriceMillions } from '../../utils';
import { useTeamPageMode, usePlayerProfileSummary } from '../../hooks';
import { ArrowUpDown, X } from 'lucide-react';

const _PlayerFiltersViewCVA = cva([''], {
  variants: {
    fullScreen: {
      true: '',
      false: '',
    },
  },
});

export type PlayerFiltersPosition = 'ALL' | 'GK' | 'DEF' | 'MID' | 'STR';
export const PlayerFiltersViewDefaultAllPosition: PlayerFiltersPosition = 'ALL';

export interface PlayerFiltersViewProps extends NoNullish<
  VariantProps<typeof _PlayerFiltersViewCVA>
> {
  className?: string;

  budget: number;

  title: string | ReactNode;
  titleRowEndElement?: ReactNode;
  topContent?: ReactNode;

  positionsLabel: string;
  positions?: PlayerFiltersPosition[];
  defaultSelectedPositions?: PlayerFiltersPosition[];
  onChangePositionFilters: (players: Player[], positions: PlayerFiltersPosition[]) => Player[];

  teamsLabel: string;
  teams: DropdownOption[];
  defaultSelectedTeams?: DropdownOption[];
  teamsMultiple?: boolean;
  onChangeTeamFilters: (players: Player[], filters: DropdownOption['value'][]) => Player[];
  // onChangeTeam: (team: DropdownOption, selected: boolean) => void;

  pricesLabel: string;
  prices: DropdownOption[];
  defaultSelectedPrices?: DropdownOption[];
  onChangePriceFilters: (
    players: Player[],
    filters: DropdownOption['value'][],
    budget: number
  ) => Player[];
  // onChangePrice: (price: DropdownOption, selected: boolean) => void;

  sortsLabel: string;
  // sorts?: DropdownOption[];
  // onChangeSort: (sort: DropdownOption, selected: boolean) => void;
  onSearchPlayers?: (players: Player[], searchText: string) => Player[];
  onPlayerSearch?: () => void;
  onSortPlayers?: (players: Player[], sortBy: DropdownOption['value']) => Player[];
  onPositionToggle?: (filterName: string) => void;
  onFilterSelect?: (filterName: string, parameter: string) => void;
  onSortSelect?: (filterName: string) => void;
  onListSortToggle?: () => void;
  onClearFilters?: (hasNoResults: boolean) => void;
  onPlayerCardIconClick?: (iconName: string, player: Player) => void;
  onPlayerCardAction?: (player: Player, mode: 'add' | 'remove') => void;
  getPlayerTeamLabel?: (player: Player) => string | undefined;
  getProfileNextFixtures?: (player: Player) => NextFixtureItem[];

  players: Player[];
  isLoading?: boolean;
  onSelectPlayer: (player: Player, source?: PlayerSelectionSource) => void;
  onRemoveSelectedPlayer?: (player: Player) => void;
  selectedPlayers?: Player['id'][];
  disabledPlayerIds?: Player['id'][];
  onDisabledPlayerAction?: (player: Player) => void;
  disabledActionFeedbackPlayerId?: Player['id'];
  disabledActionFeedbackMessage?: string;
  selectedRowVariant?: 'default' | 'twelfthMan' | 'teamSelection';
  teamSize: number;
  fixturesData?: Map<string | number, string>;

  ref?: Ref<PlayerFiltersViewRef | null>;
}

export interface CurrentPeriodFixture {
  matchId: string;
  opponentId: string;
  opponentName: string;
  opponentShortName: string | null;
  contestantFlagKey: string | null;
  isHome: boolean;
  points: number | null;
  goalBonusNormalPoints: number | null;
  goals?: number | null;
  showDNP?: boolean;
  gameweek?: number;
  matchday?: number;
  status: string;
  kickoffAt: string;
  competitionName?: string | null;
}

export type Player = {
  id: string | number;
  squadId: string | number;
  hasLeft?: boolean;
  country?: string;
  contestantFlagKey?: string;
  clubName?: string;
  shirtKey?: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  shortLastName?: string | null;
  matchName?: string | null;
  position: PlayerFiltersPosition;
  status: string;
  price: number;
  totalPoints: number;
  averagePoints: number;
  gameweekPoints: number;
  matchdayPoints?: number;
  goalBonusNormalPoints?: number;
  bonusPoints: number;
  last3Average: number;
  percentSelected: number;
  priceChange: number;
  currentPeriodFixtures?: CurrentPeriodFixture[];
  nextFixtures?: CurrentPeriodFixture[];
  suspensionDetails: {
    type: string;
    startDate: string;
    endDate: string | null;
    matches: number;
    description: string;
  } | null;
  injuryDetails: {
    type: string;
    startDate: string;
    endDate: string | null;
    expectedEndDate: string;
    status: string;
  } | null;
};

// eslint-disable-next-line react-refresh/only-export-components
export const DefaultPositions: PlayerFiltersPosition[] = ['ALL', 'GK', 'DEF', 'MID', 'STR'];

export const DefaultSorts: DropdownOption[] = [
  { id: 'price', label: 'Price', value: 'price' },
  { id: 'percentSelected', label: '% Selected', value: 'percentSelected' },
  { id: 'totalPoints', label: 'Total Pts', value: 'totalPoints' },
  { id: 'gameweekPoints', label: 'GW Pts', value: 'gameweekPoints' },
] as const;

const PositionFilterLabels: Record<Exclude<PlayerFiltersPosition, 'ALL'>, string> = {
  GK: 'Goalkeepers',
  DEF: 'Defenders',
  MID: 'Midfielders',
  STR: 'Strikers',
};

// eslint-disable-next-line react-refresh/only-export-components
export const PlayerFiltersViewDefaultAllOption: DropdownOption = {
  id: 'All',
  label: 'All',
  value: 'all',
};

const filterAllLogic = (
  prev: DropdownOption['value'][],
  allOptionValue: DropdownOption['value'],
  currOptionValue: DropdownOption['value'],
  checked: boolean
) => {
  let _prev = [...prev];
  const allOptionIndex = _prev.findIndex(v => v === allOptionValue);

  // ALL
  if (currOptionValue === allOptionValue || allOptionIndex > -1) {
    if (checked && currOptionValue === allOptionValue) {
      _prev = [allOptionValue];
    } else {
      _prev = [currOptionValue];
    }
  } else if (!checked) {
    const _idx = _prev.findIndex(o => o === currOptionValue);
    _prev.splice(_idx, 1);
  } else {
    _prev.push(currOptionValue);
  }
  // ? If the list empty, reset to All
  if (_prev.length === 0) {
    return [allOptionValue];
  }
  return _prev;
};

export interface PlayerFiltersViewRef {
  updateSelectedPositions: (_position: PlayerFiltersPosition[]) => void;
}

export type PlayerSelectionSource = 'player-list' | 'player-card';

export const PlayerFiltersView = ({
  className,
  fullScreen = false,

  title,
  titleRowEndElement,
  topContent,

  positionsLabel,
  positions = DefaultPositions,
  defaultSelectedPositions = [],
  onChangePositionFilters,

  teams: propTeams,
  teamsLabel,
  defaultSelectedTeams,
  teamsMultiple = false,
  onChangeTeamFilters,
  // onChangeTeam,

  prices: propPrices,
  pricesLabel,
  defaultSelectedPrices,
  onChangePriceFilters,
  // onChangePrice,

  // sorts,
  sortsLabel,
  // onChangeSort,
  onSearchPlayers,
  onPlayerSearch,
  onSortPlayers,
  onPositionToggle,
  onFilterSelect,
  onSortSelect,
  onListSortToggle,
  onClearFilters,
  onPlayerCardIconClick,
  onPlayerCardAction,
  getPlayerTeamLabel,
  getProfileNextFixtures,

  players: propPlayers,
  isLoading = false,
  onSelectPlayer,
  onRemoveSelectedPlayer,
  selectedPlayers = [],
  disabledPlayerIds = [],
  onDisabledPlayerAction,
  disabledActionFeedbackPlayerId,
  disabledActionFeedbackMessage,
  selectedRowVariant = 'default',
  teamSize,
  fixturesData,
  budget,

  ref,
}: PlayerFiltersViewProps) => {
  const { isCreateMode, isTeamConfirmed } = useTeamPageMode();
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchText, setSearchText] = useState<string>('');
  const [profilePlayer, setProfilePlayer] = useState<Player | undefined>();
  const [selectedTeams, setSelectedTeams] = useState<DropdownOption['value'][]>(
    defaultSelectedTeams?.length
      ? teamsMultiple
        ? defaultSelectedTeams.map(({ value }) => value)
        : [defaultSelectedTeams[0].value]
      : [PlayerFiltersViewDefaultAllOption.value]
  );
  const [selectedPositions, setSelectedPositions] = useState<PlayerFiltersPosition[]>(
    defaultSelectedPositions ? defaultSelectedPositions : [PlayerFiltersViewDefaultAllPosition]
  );
  const [selectedPrices, setSelectedPrices] = useState<DropdownOption['value'][]>(
    defaultSelectedPrices
      ? defaultSelectedPrices.map(({ value }) => value)
      : [PlayerFiltersViewDefaultAllOption.value]
  );
  const [selectedSorts, setSelectedSort] = useState<DropdownOption['value'][]>([
    DefaultSorts[0].value,
  ]);
  const [priceSortDirection, setPriceSortDirection] = useState<'asc' | 'desc'>('desc');

  useImperativeHandle(
    ref,
    () => ({
      updateSelectedPositions: setSelectedPositions,
    }),
    []
  );

  const onPositionValueChange = useCallback(
    (v: string, checked: boolean) => {
      onPositionToggle?.(v);
      setSelectedPositions(prev => {
        return filterAllLogic(
          prev,
          PlayerFiltersViewDefaultAllPosition,
          v,
          checked
        ) as PlayerFiltersPosition[];
      });
    },
    [onPositionToggle]
  );

  const onTeamValueChange = useCallback(
    (option: DropdownOption, checked: boolean) => {
      onFilterSelect?.('Team', option.label);
      setSelectedTeams(prev => {
        if (!teamsMultiple) {
          if (!checked || option.value === PlayerFiltersViewDefaultAllOption.value) {
            return [PlayerFiltersViewDefaultAllOption.value];
          }

          return [option.value];
        }

        return filterAllLogic(prev, PlayerFiltersViewDefaultAllOption.value, option.value, checked);
      });
    },
    [onFilterSelect, teamsMultiple]
  );

  const onPriceValueChange = useCallback(
    (option: DropdownOption, checked: boolean) => {
      onFilterSelect?.('Price', option.label);
      setSelectedPrices(prev => {
        return filterAllLogic(prev, PlayerFiltersViewDefaultAllOption.value, option.value, checked);
      });
    },
    [onFilterSelect]
  );

  const onSortValueChange = useCallback(
    (option: DropdownOption) => {
      onSortSelect?.(option.label);
      setSelectedSort([option.value]);
    },
    [onSortSelect]
  );

  const players = useMemo(() => {
    let _players = onSearchPlayers
      ? onSearchPlayers(propPlayers, searchText)
      : searchText?.trim().length === 0
        ? propPlayers
        : propPlayers.filter(player => {
            return player.displayName.toLowerCase().includes(searchText.toLowerCase());
          });
    _players = onChangePositionFilters(_players, selectedPositions);
    _players = onChangeTeamFilters(_players, selectedTeams);
    _players = onChangePriceFilters(_players, selectedPrices, budget);
    _players = onSortPlayers
      ? onSortPlayers(_players, selectedSorts[0] ?? DefaultSorts[0].value)
      : _players;

    if ((selectedSorts[0] ?? DefaultSorts[0].value) === 'price') {
      _players = [..._players].sort((left, right) => {
        return priceSortDirection === 'asc' ? left.price - right.price : right.price - left.price;
      });
    }
    if ((selectedSorts[0] ?? DefaultSorts[0].value) === 'percentSelected') {
      _players = [..._players].sort((left, right) => {
        return priceSortDirection === 'asc'
          ? left.percentSelected - right.percentSelected
          : right.percentSelected - left.percentSelected;
      });
    }
    if ((selectedSorts[0] ?? DefaultSorts[0].value) === 'points') {
      _players = [..._players].sort((left, right) => {
        return priceSortDirection === 'asc'
          ? left.totalPoints - right.totalPoints
          : right.totalPoints - left.totalPoints;
      });
    }

    return _players;
  }, [
    budget,
    onChangePositionFilters,
    onChangePriceFilters,
    onSearchPlayers,
    onSortPlayers,
    onChangeTeamFilters,
    propPlayers,
    searchText,
    selectedPositions,
    selectedPrices,
    selectedSorts,
    priceSortDirection,
    selectedTeams,
  ]);

  const openPlayerProfile = useCallback(
    (iconName: string, player: Player) => {
      onPlayerCardIconClick?.(iconName, player);
      setProfilePlayer(player);
    },
    [onPlayerCardIconClick]
  );

  const onPressPlayer = useCallback(
    (player: Player) => {
      if (disabledPlayerIds.some(playerId => playerId === player.id)) {
        onDisabledPlayerAction?.(player);
        return;
      }
      onSelectPlayer?.(player, 'player-list');
    },
    [disabledPlayerIds, onDisabledPlayerAction, onSelectPlayer]
  );

  const onPressProfile = useCallback(
    (player: Player) => {
      openPlayerProfile('Player Name', player);
    },
    [openPlayerProfile]
  );

  const onPressAction = useCallback(
    (player: Player, mode: 'add' | 'remove') => {
      onPlayerCardAction?.(player, mode);
      if (disabledPlayerIds.some(playerId => playerId === player.id)) {
        onDisabledPlayerAction?.(player);
        return;
      }
      if (mode === 'remove') {
        onRemoveSelectedPlayer?.(player);
        return;
      }
      onSelectPlayer?.(player, 'player-list');
    },
    [
      disabledPlayerIds,
      onDisabledPlayerAction,
      onPlayerCardAction,
      onRemoveSelectedPlayer,
      onSelectPlayer,
    ]
  );

  const onPressJersey = useCallback(
    (player: Player) => {
      openPlayerProfile('Shirt', player);
    },
    [openPlayerProfile]
  );

  const onProfileModalOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setProfilePlayer(undefined);
    }
  }, []);

  const teams = useMemo(() => {
    return [PlayerFiltersViewDefaultAllOption, ...propTeams];
  }, [propTeams]);

  const prices = useMemo(() => {
    return [PlayerFiltersViewDefaultAllOption, ...propPrices];
  }, [propPrices]);

  const handlePriceHeaderSortToggle = useCallback(() => {
    onListSortToggle?.();
    setPriceSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
  }, [onListSortToggle]);

  const getValueLabel = useCallback((value: string | number, options: DropdownOption[]) => {
    return options.find(option => String(option.value) === String(value))?.label ?? String(value);
  }, []);

  const getResolvedPlayerTeamLabel = useCallback(
    (player: Player) => getPlayerTeamLabel?.(player) ?? player.clubName ?? player.country,
    [getPlayerTeamLabel]
  );

  const activeFilterChips = useMemo(() => {
    const chips: Array<
      | { key: string; label: string; type: 'position'; value: PlayerFiltersPosition }
      | { key: string; label: string; type: 'team'; value: string | number }
      | { key: string; label: string; type: 'price'; value: string | number }
    > = [];

    selectedPositions
      .filter(position => position !== PlayerFiltersViewDefaultAllPosition)
      .forEach(position => {
        const label =
          position === 'ALL'
            ? 'All Positions'
            : (PositionFilterLabels[position as Exclude<PlayerFiltersPosition, 'ALL'>] ?? position);
        chips.push({
          key: `position:${position}`,
          label,
          type: 'position',
          value: position,
        });
      });

    selectedTeams
      .filter(value => String(value) !== String(PlayerFiltersViewDefaultAllOption.value))
      .forEach(value => {
        chips.push({
          key: `team:${value}`,
          label: getValueLabel(value, teams),
          type: 'team',
          value,
        });
      });

    selectedPrices
      .filter(value => String(value) !== String(PlayerFiltersViewDefaultAllOption.value))
      .forEach(value => {
        chips.push({
          key: `price:${value}`,
          label: getValueLabel(value, prices),
          type: 'price',
          value,
        });
      });

    return chips;
  }, [getValueLabel, prices, selectedPositions, selectedPrices, selectedTeams, teams]);

  const removeFilterValue = useCallback(
    (type: 'position' | 'team' | 'price', value: string | number) => {
      if (type === 'position') {
        setSelectedPositions(current => {
          const next = current.filter(item => item !== value);
          return next.length ? next : [PlayerFiltersViewDefaultAllPosition];
        });
        return;
      }

      if (type === 'team') {
        setSelectedTeams(current => {
          const next = current.filter(item => String(item) !== String(value));
          return next.length ? next : [PlayerFiltersViewDefaultAllOption.value];
        });
        return;
      }

      setSelectedPrices(current => {
        const next = current.filter(item => String(item) !== String(value));
        return next.length ? next : [PlayerFiltersViewDefaultAllOption.value];
      });
    },
    []
  );

  const clearAllFilters = useCallback(() => {
    onClearFilters?.(players.length === 0);
    setSelectedPositions([PlayerFiltersViewDefaultAllPosition]);
    setSelectedTeams([PlayerFiltersViewDefaultAllOption.value]);
    setSelectedPrices([PlayerFiltersViewDefaultAllOption.value]);
  }, [onClearFilters, players.length]);

  const profileHero = useMemo(() => {
    if (!profilePlayer) {
      return undefined;
    }

    const priceChange = profilePlayer.priceChange ?? 0;

    return {
      firstName: profilePlayer.firstName ?? '',
      lastName: profilePlayer.lastName ?? '',
      position: profilePlayer.position,
      price: toCurrencyPriceMillions(profilePlayer.price),
      priceChange: `${priceChange > 0 ? '+' : priceChange < 0 ? '-' : ''}£${toPriceMillions(Math.abs(priceChange))}`,
      priceTrend:
        priceChange > 0 ? ('up' as const) : priceChange < 0 ? ('down' as const) : ('flat' as const),
      teamLabel: profilePlayer.country ?? 'N/A',
      shirt: (
        <TeamShirt
          team={profilePlayer.shirtKey ?? profilePlayer.country}
          position={profilePlayer.position}
          assetScale="1x"
          className="absolute top-2 h-[111px] w-[111px] shrink-0"
          loading="eager"
        />
      ),
      teamFlag: (
        <CountryFlag
          className="h-full w-full object-cover"
          country={profilePlayer.contestantFlagKey ?? profilePlayer.country ?? 'WW'}
        />
      ),
    };
  }, [profilePlayer]);

  const profileSummary = usePlayerProfileSummary(profilePlayer);

  const profileNextFixtures = useMemo(() => {
    if (!profilePlayer) {
      return [];
    }

    if (getProfileNextFixtures) {
      return getProfileNextFixtures(profilePlayer);
    }

    return ['Matchday 1', 'Matchday 2', 'Matchday 3'].map(label => {
      return {
        label,
        opponentName: 'TBC',
        opponentFlag: (
          <CountryFlag className="h-full w-full object-cover" country="" fallbackCountry="" />
        ),
      };
    });
  }, [profilePlayer, getProfileNextFixtures]);

  return (
    <div
      className={twMerge(
        [
          fullScreen ? 'h-full w-full' : 'h-[668px]',
          'flex',
          'flex-col',
          fullScreen ? 'rounded-none' : 'rounded-[--corner-md] w-[411px]',
          'overflow-hidden',
        ],
        className
      )}
    >
      <div className="flex flex-row items-center py-[--padding-sm] px-[--padding-md] border-b border-[--playerfilters-border]">
        <div
          className={twMerge('flex flex-1 grow overflow-x-auto gap-[--padding-md] items-center')}
        >
          {typeof title === 'string' ? (
            <Typography variant={'label050'} className="font-bold">
              {title}
            </Typography>
          ) : (
            title
          )}
        </div>
        {titleRowEndElement}
      </div>
      {topContent ? <div className="px-[--padding-md] pt-[--padding-md]">{topContent}</div> : null}

      {/* Position filters */}
      <div
        className={twMerge(
          'flex flex-row items-center py-[--padding-lg] px-[--padding-md] gap-[--padding-xl]',
          searchExpanded ? 'gap-[0px]' : ''
        )}
      >
        {positions ? (
          <div
            className={twMerge([
              'grid gap-[--padding-xs] grow',
              searchExpanded ? 'overflow-x-hidden grow-0 w-0' : '',
            ])}
          >
            <Typography>{positionsLabel}</Typography>
            <div className="flex flex-1 gap-[--padding-xs]">
              {positions?.map(position => {
                const selected = selectedPositions.some(pos => pos === position);
                return (
                  <Tab
                    key={position}
                    color="darkBlue"
                    appearance={'pill'}
                    selected={selected}
                    onClick={() => {
                      onPositionValueChange?.(position, !selected);
                    }}
                  >
                    {position}
                  </Tab>
                );
              })}
            </div>
          </div>
        ) : null}
        <SearchInputField
          fieldsetClassName={'flex flex-1'}
          id={'playerSearch'}
          label="Search"
          defaultCompact
          onChange={e => setSearchText(e.target.value)}
          onExpand={() => {
            setSearchExpanded(true);
            onPlayerSearch?.();
          }}
          onCollapse={() => {
            setSearchExpanded(false);
            setSearchText('');
          }}
        />
      </div>

      <div
        className={twMerge(
          'grid  px-[--padding-md] gap-[--padding-sm]',
          'grid-cols-3',
          'content-start'
        )}
      >
        <Dropdown
          label={teamsLabel}
          options={teams}
          values={selectedTeams}
          multiple={teamsMultiple}
          onValueChange={onTeamValueChange}
          className="flex flex-1 grow-0"
          onWhite
        />
        <Dropdown
          label={pricesLabel}
          options={prices}
          values={selectedPrices}
          multiple
          onValueChange={onPriceValueChange}
          className="flex flex-1 grow-0"
          onWhite
        />
        <Dropdown
          label={sortsLabel}
          options={DefaultSorts}
          values={selectedSorts}
          multiple={false}
          onValueChange={onSortValueChange}
          className="flex flex-1 grow-0"
          onWhite
        />
      </div>

      {/* Filters row */}
      {activeFilterChips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-[8px] border-b border-[--playerfilters-border] px-[--padding-md] py-[--padding-sm]">
          <Typography
            variant="paragraph010"
            className="text-[10px] leading-[12px] font-normal text-[--color-neutral-600]"
          >
            Filters:
          </Typography>
          {activeFilterChips.map(chip => (
            <button
              key={chip.key}
              type="button"
              onClick={() => {
                const filterName =
                  chip.type === 'position' ? 'Position' : chip.type === 'team' ? 'Team' : 'Price';
                onFilterSelect?.(filterName, `Removed ${chip.label}`);
                removeFilterValue(chip.type, chip.value);
              }}
              className="inline-flex h-[26px] items-center gap-[4px] rounded-full border border-[--button-contained-blue-base-bg] px-[8px] text-[--button-contained-blue-base-bg]"
              aria-label={`Remove ${chip.label} filter`}
            >
              <Typography
                variant="button020"
                className="text-[11px] leading-[14px] font-normal text-[--button-contained-blue-base-bg]"
              >
                {chip.label}
              </Typography>
              <X size={10} />
            </button>
          ))}
          <button
            type="button"
            onClick={clearAllFilters}
            className="inline-flex h-[26px] items-center rounded-[8px] bg-[--button-contained-blue-base-bg] px-[10px] text-white"
          >
            <Typography
              variant="button020"
              className="text-[11px] leading-[14px] font-normal text-white"
            >
              Clear Filters
            </Typography>
          </button>
        </div>
      ) : null}

      {/* Filters header */}
      <div className="flex items-center flex-row px-[--padding-md] pt-[--padding-lg] pb-[--padding-sm] border-b border-[--playerfilters-border] gap-[--padding-xs]">
        <Typography variant={'paragraph010'} className="flex flex-1 grow">
          Player
        </Typography>
        <div className="flex flex-1 items-center justify-end">
          <button
            type="button"
            aria-label="Sort by price"
            className="flex items-center gap-[4px] border-none bg-transparent p-0 outline-none"
            onClick={handlePriceHeaderSortToggle}
          >
            <Typography variant={'paragraph010'}>
              {DefaultSorts.find(s => s.id === selectedSorts[0])?.label}
            </Typography>
            <ArrowUpDown size={12} />
          </button>
        </div>
      </div>

      <div className="flex min-h-[260px] md:min-h-[260px] flex-1 flex-col">
        {isLoading ? (
          <div
            role="status"
            aria-live="polite"
            className="flex h-full flex-col items-center justify-center gap-[--padding-sm] px-[--padding-md] text-[--button-contained-blue-base-bg]"
          >
            <Loading height={32} width={32} className="animate-spin" />
            <Typography variant="paragraph020" className="text-[--color-neutral-700]">
              Loading players...
            </Typography>
          </div>
        ) : (
          <List
            className="h-full overflow-y-auto"
            rowComponent={PlayerFiltersViewRowItem}
            rowCount={players.length}
            rowHeight={32 + 12 + 12}
            rowProps={{
              players,
              sortBy: selectedSorts?.[0],
              budget,
              selectedPlayers,
              disabledPlayerIds,
              disabledActionFeedbackPlayerId,
              disabledActionFeedbackMessage,
              selectedRowVariant,
              mode: selectedPlayers.length === teamSize ? 'removable' : 'selection',
              fixturesData,
              onPressPlayer,
              onPressProfile,
              onPressJersey,
              onPressAction,
              getPlayerTeamLabel: getResolvedPlayerTeamLabel,
            }}
          />
        )}
      </div>
      {profileHero ? (
        <PlayerProfileModal
          open
          onOpenChange={onProfileModalOpenChange}
          hero={profileHero}
          summary={profileSummary}
          nextFixtures={profileNextFixtures}
          hidePrimaryAction={
            isCreateMode && !!profilePlayer && selectedPlayers.some(id => id === profilePlayer.id)
          }
          primaryActionLabel={isCreateMode ? 'Add to Team' : 'Transfer Out'}
          primaryActionIcon={
            isCreateMode ? <AddPlayer className="size-4 text-white" aria-hidden /> : undefined
          }
          primaryActionDisabled={isCreateMode && isTeamConfirmed}
          onPrimaryAction={
            isCreateMode
              ? () => {
                  if (!profilePlayer || isTeamConfirmed) return;

                  onSelectPlayer(profilePlayer);
                  setProfilePlayer(undefined);
                }
              : undefined
          }
          showCaptainActions={false}
        />
      ) : null}
    </div>
  );
};
